import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

type DebouncedFn<A extends unknown[]> = ((...args: A) => void) & {
  cancel: () => void;
  flush: () => void;
};

/**
 * A debounced callback that cancels itself on unmount.
 *
 * Replaces the `useCallback(debounce(fn, ms), deps)` pattern, which had two
 * real defects wherever it was used:
 *
 *   1. Nothing cancelled the pending call, so a queued Firestore write could
 *      fire after the component unmounted.
 *   2. When `deps` changed, a *new* debounced function was created while the
 *      previous one's timer stayed armed, so a stale payload could still land.
 *
 * The latest `fn` is held in a ref, so the debounced wrapper is stable for the
 * lifetime of `delay` and callers do not need a dependency array at all — which
 * also removes the `useExhaustiveDependencies` suppressions those sites carried.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  delay: number
): DebouncedFn<A> {
  const latest = useRef(fn);

  useEffect(() => {
    latest.current = fn;
  }, [fn]);

  const debounced = useMemo(
    () => debounce((...args: A) => latest.current(...args), delay),
    [delay]
  ) as DebouncedFn<A>;

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}

export default useDebouncedCallback;
